/**
 * Pure helpers for the "Comments and Activity" feed. Items are keyed by id,
 * so an API response and the socket echo of the same event never show twice.
 */

// Mirrors the API's feed filters.
export const FEED_FILTERS = {
  all: null,
  comments: ["comment_added"],
  history: ["task_created", "field_changed"],
  files: ["attachment_added", "attachment_removed"],
};

export const matchesFeedFilter = (item, filter) =>
  !FEED_FILTERS[filter] || FEED_FILTERS[filter].includes(item.type);

const newestFirst = (a, b) =>
  new Date(b.createdAt) - new Date(a.createdAt) ||
  (a._id < b._id ? 1 : a._id > b._id ? -1 : 0);

/** Merge new items into the list (dedupe by id, newest first). */
export const mergeFeed = (items, incoming) => {
  const byId = new Map(items.map((i) => [i._id, i]));
  incoming.forEach((i) => byId.set(i._id, { ...byId.get(i._id), ...i }));
  return [...byId.values()].sort(newestFirst);
};

/** Replace the embedded comment wherever it appears (edit / delete). */
export const applyCommentUpdate = (items, comment) =>
  items.map((i) =>
    i.comment && i.comment._id === comment._id ? { ...i, comment } : i,
  );

/** Apply attachment changes (e.g. removal) to file events and comment files. */
export const applyAttachmentUpdates = (items, attachments) => {
  const byId = new Map(attachments.map((a) => [a._id, a]));
  return items.map((i) => {
    let next = i;
    if (i.attachment && byId.has(i.attachment._id))
      next = { ...next, attachment: byId.get(i.attachment._id) };
    if (i.comment?.attachments?.some((a) => byId.has(a._id))) {
      next = {
        ...next,
        comment: {
          ...i.comment,
          attachments: i.comment.attachments.filter(
            (a) => !byId.get(a._id)?.deletedAt,
          ),
        },
      };
    }
    return next;
  });
};

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/** Group (already sorted) items under Today / Yesterday / a date heading. */
export const groupByDay = (items, now = new Date()) => {
  const today = dayKey(now);
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  const yesterday = dayKey(y);
  const groups = [];
  items.forEach((item) => {
    const d = new Date(item.createdAt);
    const key = dayKey(d);
    const label =
      key === today
        ? "Today"
        : key === yesterday
          ? "Yesterday"
          : d.toLocaleDateString(undefined, {
              weekday: "short",
              day: "numeric",
              month: "short",
              year: "numeric",
            });
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(item);
    else groups.push({ key, label, items: [item] });
  });
  return groups;
};
